import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, switchMap, timer } from 'rxjs';
import { skipErrorHandlerContext } from 'src/app/core/auth/interceptors/skip-error-handler.token';
import { UserAuthorizationService } from 'src/app/core/auth/services/user-authorization.service';
import { ApiClient } from 'src/app/shared/services/common/api-client/api-client.service';
import { ApiController } from 'src/app/shared/services/common/api-urls/api-controller.enum';
import { apiUrl } from 'src/app/shared/services/common/api-urls/api-urls';
import { Constants } from 'src/environments/global-settings';

@Injectable({
	providedIn: 'root',
})
export class BackendHealthMonitorService {
	private readonly destroyRef = inject(DestroyRef);
	private readonly api = inject(ApiClient);
	private readonly url = apiUrl(ApiController.Health);
	private readonly authorizationService = inject(UserAuthorizationService);

	private readonly _healthState = signal<HealthState>({
		isHealthy: true,
		lastChecked: null,
		isChecking: false,
		consecutiveFailures: 0,
	});

	private readonly _monitoringStarted = signal(false);

	readonly isBackendHealthy = computed(() => this._healthState().isHealthy);
	readonly lastChecked = computed(() => this._healthState().lastChecked);
	readonly isChecking = computed(() => this._healthState().isChecking);
	readonly needsAttention = computed(() => this._healthState().consecutiveFailures > 3);
	readonly monitoringStarted = computed(() => this._monitoringStarted());

	private readonly _initializeMonitoring = effect(() => {
		if (this.authorizationService.isLoggedIn() && !this._monitoringStarted()) {
			this._monitoringStarted.set(true);
			this.startHealthMonitoring();
		}
	});

	private startHealthMonitoring(): void {
		timer(Constants.HEALTHCHECK_INTERVAL, Constants.HEALTHCHECK_INTERVAL)
			.pipe(
				switchMap(() => this.checkBackendHealth()),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	private updateHealthState(updates: Partial<HealthState>): void {
		this._healthState.update((state) => ({
			...state,
			...updates,
		}));
	}

	/**
	 * Asks the API health endpoint whether the backend answers. A failed check is reported by the health banner
	 * (`app-health-status`), so the request opts out of the global error toast.
	 */
	checkBackendHealth(): Observable<boolean> {
		this.updateHealthState({ isChecking: true });

		return this.api.head(this.url, skipErrorHandlerContext()).pipe(
			map(() => {
				this.updateHealthState({
					isHealthy: true,
					lastChecked: new Date(),
					isChecking: false,
					consecutiveFailures: 0,
				});
				return true;
			}),
			catchError(() => {
				this.updateHealthState({
					isHealthy: false,
					lastChecked: new Date(),
					isChecking: false,
					consecutiveFailures: this._healthState().consecutiveFailures + 1,
				});
				return of(false);
			})
		);
	}

	getHealthState(): HealthState {
		return this._healthState();
	}
}

export interface HealthState {
	isHealthy: boolean;
	lastChecked: Date | null;
	isChecking: boolean;
	consecutiveFailures: number;
}
